// ─────────────────────────────────────────────────────────────────────────────
// READING AN ADJUSTER'S CORN APPRAISAL
//
// Kevin, 2026-09-24: *"build ability to ingest this silage appraisal document from NAU,
// attribute it to the proper farm, and sum total bushels of appraisals. This may not be
// all of their acres so dont flag as claim yet, but store it as silage appraisals."*
//
// ⚠ THE FORM SAYS "GRAIN", AND THAT IS NOT A CONTRADICTION. NAU's is headed *Corn Sorghum
// Appraisal · Part II — Mature Line Weight Method (for ear corn from milk stage to 40%
// moisture)* and the coverage line reads `Type: 016-Grain`. That is what a chop appraisal
// IS: the corn is insured as grain, the grower wants to chop it for silage, and the
// adjuster appraises the GRAIN yield that would have been harvested so it can be counted
// as production. So the number this yields is BUSHELS OF GRAIN — never tons of silage —
// and nothing here may label it otherwise.
//
// ⚠ AND IT IS NOT A CLAIM. Kevin: "This may not be all of their acres so dont flag as
// claim yet." An appraisal covers the fields the adjuster walked, which is a subset of
// the unit until the adjuster says otherwise. `coversWholeUnit` is therefore never
// inferred from the document — the document cannot know.
// ─────────────────────────────────────────────────────────────────────────────

export const CARRIER = Object.freeze({ NAU: "NAU", RAIN_AND_HAIL: "RAIN_AND_HAIL" });

const num = (v) => {
  if (v == null) return null;
  const n = Number(String(v).replace(/[, ]/g, ""));
  return Number.isFinite(n) ? n : null;
};
const clean = (s) => String(s == null ? "" : s).replace(/\s+/g, " ").trim();

// ── WHICH CARRIER'S FORM IS THIS ─────────────────────────────────────────────
// Identified by what the document SAYS, never by the filename. Kevin's own words on the
// 426: an export is identified by measurement. A form we cannot identify is REFUSED —
// parsing an R&H layout with NAU's field positions would produce confident nonsense.
export function detectCarrier(text) {
  const t = String(text || "");
  if (/naucountry\.com/i.test(t) || /NAU Country/i.test(t)) return CARRIER.NAU;
  if (/Rain\s*(and|&)\s*Hail/i.test(t) || /rainhail\.com/i.test(t)) return CARRIER.RAIN_AND_HAIL;
  return null;
}

// ── THE HEADER ───────────────────────────────────────────────────────────────
export function parseHeader(text) {
  const t = String(text || "");
  const one = (re) => { const m = re.exec(t); return m ? clean(m[1]) : null; };
  const policyNumber = one(/Policy\s*(?:Number|#)\s*:?\s*([A-Z0-9-]+)/i);
  const claimNumber = one(/Claim\s*Number\s*:?\s*([A-Z0-9-]+)/i);
  const cropYear = num(one(/Crop\s*Year\s*:?\s*(\d{4})/i));
  // "069-Ontario" — the code is RMA's, the name is the label. Both kept.
  // ⚠ STOP AT THE COLUMN BOUNDARY, for the same reason the insured name does. A greedy
  // name class swallows the next column: "069-Ontario        Unit No.: 0001-0001-000"
  // parsed as the county "ONTARIO UNIT NO.". A run of two or more spaces, a newline, or
  // the next labelled field ends it.
  const cm = /County\s*:?\s*\n?\s*(\d{3})\s*-\s*([A-Za-z .'-]+?)(?=\s{2,}|\s*\n|\s*$|\s+\w+\s*:)/.exec(t);
  const created = one(/Created\s*:?\s*(\d{2}\/\d{2}\/\d{4})/i);
  // The insured's name sits on its own line under "Insured Information"; take the first
  // non-empty line after it rather than guessing an offset.
  // ⚠ CUT THE COLUMN BEFORE COLLAPSING THE WHITESPACE. The form is three columns wide and
  // a text extraction puts them on one line:
  //   "ROGERS FARMS LLC            NY            Agent: MICHAEL J MEISENZAHL"
  // Collapsing runs of spaces first destroys the only thing separating the insured from
  // the state and the agent, and the name came out as all three joined together. The run
  // of spaces IS the column boundary, so it has to be read before it is normalised.
  let insured = null;
  const ii = t.indexOf("Insured Information");
  if (ii >= 0) {
    for (const l of t.slice(ii + 19).split(/\r?\n/)) {
      const firstCol = clean(String(l).replace(/\s{2,}[\s\S]*$/, ""));
      if (firstCol && !/^(State|County|Crop Year|Agency|Agent|P:|E:|F:)\b/i.test(firstCol)) { insured = firstCol; break; }
    }
  }
  return {
    carrier: detectCarrier(t),
    policyNumber, claimNumber, cropYear,
    countyCode: cm ? cm[1] : null,
    county: cm ? clean(cm[2]).toUpperCase() : null,
    insuredName: insured ? insured.toUpperCase() : null,
    createdDate: created,
  };
}

// ── THE APPRAISAL BLOCKS ─────────────────────────────────────────────────────
//
// One block per field walked. The layout that matters, verified against ROGERS FARMS LLC
// NY-944-1014132-26 (11 pages, 17 blocks):
//
//   County: 069-Ontario   Unit No.: 0001-0001-000   Crop: 0041-CORN  Type: 016-Grain  FSA Farm No. 7661
//   1             1/4           1000                     <- the field ID leads the 1/4 row
//   9.5           Doughy        1000                     <- the ACREAGE leads the Doughy row
//   877.8 ÷ 4 = 219.5                                    <- total ÷ rep samples = bu/ac
//
// ⚠ THE ACREAGE IS ON THE `Doughy` ROW, NOT UNDER THE "Acreage in Field to Tenths" LABEL.
// The label is split across three lines of its own column and the value sits one row
// below it, which in a text extraction lands at the head of the Doughy line. Reading the
// line after the label instead picks up plot weights.
const BLOCK_END = /([\d,]+\.?\d*)\s*[^\S\n]*[÷·�\/]\s*([\d]+)\s*=\s*([\d,]+\.?\d*)/;

// ⚠ THE SAME ROW, WITH THE OPERATORS ON A LINE OF THEIR OWN (2026-09-24).
//
// The pattern above reads `686.1 ÷ 3 = 228.7`, which is how this row arrives from a
// column-preserving text dump. The BROWSER does not produce that. pdf.js groups text by
// baseline, and on this form the ÷ and = are pre-printed furniture sitting a fraction
// lower than the numbers typed into it, so one visual row arrives as two lines:
//
//     "686.1 3 228.7"        <- the numbers
//     "= x = ÷ ="            <- the operators
//
// Measured on the real worksheet: EVERY one of the sixteen blocks splits this way, so the
// upload read zero appraisals and fell through to the model without a word — the parser
// was wired in and doing nothing.
//
// ⚠ AND A LINE OF THREE NUMBERS IS NOT ASSUMED TO BE THE DIVISION — THE ARITHMETIC HAS TO
// HOLD. `total ÷ samples = appraisal` is checked against the numbers themselves, so this
// reads the row rather than guessing at it, and a coincidental trio elsewhere on the page
// (plot weights, a date, the Yield Factor line) cannot be mistaken for one. The tolerance
// is one unit in the last printed place, because the form prints the quotient rounded.
const BLOCK_END_SPLIT = /^\s*([\d,]+\.?\d*)\s+(\d{1,2})\s+([\d,]+\.?\d*)\s*$/;
function divisionRow(line) {
  const m = BLOCK_END_SPLIT.exec(line);
  if (!m) return null;
  const total = num(m[1]), samples = num(m[2]), appraisal = num(m[3]);
  if (!(total > 0) || !(samples > 0) || !(appraisal > 0)) return null;
  const places = (String(m[3]).split(".")[1] || "").length;
  const tol = Math.pow(10, -places) / 2 + 1e-9;
  return Math.abs(total / samples - appraisal) <= tol
    ? { total, samples, appraisal } : null;
}

// One field number: digits, optionally a letter or a dash part ("12", "7A", "3-1", "f-407").
const FIELD_TOKEN = String.raw`[A-Za-z]?-?\d[\w.-]*`;
const ID_LIST = String.raw`${FIELD_TOKEN}(?:\s*(?:,|&|and)\s*${FIELD_TOKEN})*`;
// The 1/4 row: a list of field numbers, then the 1/4 fraction and the plot size.
const FID_ROW = new RegExp(String.raw`^\s*(${ID_LIST}|\S+)\s+1\/4\s`);
// A wrapped first part of a Field ID: a list of field numbers ending in a comma or "&",
// alone on its line.
const ID_WRAP = new RegExp(String.raw`^\s*(${ID_LIST}\s*(?:,|&))\s*$`);

export function parseAppraisals(text) {
  const lines = String(text || "").split(/\r?\n/);
  const out = [];
  const refused = [];
  let ctx = {};            // the most recent coverage header
  let headerOpen = false;  // between a "Unit No." line and the first field block under it
  let pending = null;      // the block being assembled
  let idWrap = null;       // the first part of a Field ID that wrapped onto the line(s) above its 1/4 row
  // A block whose 1/4 row carried no Field ID the reader could take: its numbers are still
  // read, so it can be NAMED in `refused` with its acres and appraisal — never dropped.
  let orphan = null;

  for (const raw of lines) {
    const l = raw.replace(/\s+$/, "");

    // ⚠ EVERY "Unit No." STARTS A NEW COVERAGE CONTEXT, WHETHER OR NOT THE REST OF THE
    // HEADER PARSES. The old rule matched the whole header in one regex that REQUIRED a
    // digit after "FSA Farm No.", and NAU leaves that blank on a unit that spans several
    // farms. A header that failed to match did not reset anything, so every block under it
    // inherited the PREVIOUS unit and farm. RED DAWN FARMING LLC MI-942-6095825-26
    // (2026-10-22, 71 blocks): eleven blocks across units 0002-0056/0057/0058/0060 were
    // stored as unit 0002-0056 FSA farm 9409 — 995.2 appraised ac on a 22.0-ac farm — and
    // f-407 (151.9 ac) is in fact unit 0002-0057, FSA farm 6734's 151.9-ac field.
    // A blank farm number is stored as NULL: the worksheet does not state one, and the
    // previous unit's farm is a different farm.
    const unitHdr = /Unit No\.\s*:?\s*([\d-]+)/.exec(l);
    if (unitHdr) {
      const cty = /County\s*:?\s*(\d{3})\s*-\s*([A-Za-z .'-]+?)(?=\s{2,}|\s*$|\s+Unit No)/.exec(l);
      ctx = { unitNumber: clean(unitHdr[1]), cropCode: null, crop: null, typeCode: null, type: null,
              fsaFarmNumber: null,
              countyCode: cty ? cty[1] : null, county: cty ? clean(cty[2]).toUpperCase() : null };
      headerOpen = true;
      idWrap = null;
    }
    // The rest of the header can sit on the same line or — on the first page, where the
    // contact column pushes it down — on a following line. Read the pieces wherever they
    // land until the first field block starts.
    if (headerOpen) {
      const crop = /\bCrop:\s*(\d{4})-([A-Z ]+?)(?=\s{2,}|\s+Type:|\s*$)/.exec(l);
      if (crop) { ctx.cropCode = crop[1]; ctx.crop = clean(crop[2]).toUpperCase(); }
      const typ = /\bType:\s*(\d{3})-([A-Za-z ]+?)(?=\s{2,}|\s+FSA|\s*$)/.exec(l);
      if (typ) { ctx.typeCode = typ[1]; ctx.type = clean(typ[2]); }
      const fsa = /FSA Farm No\.?\s*(\d+)/.exec(l);
      if (fsa) ctx.fsaFarmNumber = clean(fsa[1]);
      if (unitHdr || crop || typ || /FSA Farm No/.test(l)) continue;
    }

    // ⚠ A FIELD ID IS NOT ALWAYS ONE TOKEN (2026-11-02). The adjuster writes every field
    // number the block covers: "87 & 88", "20, 21, 52", and on a long list the cell wraps,
    // so pdf.js puts "3, 4, 5, 6," a few lines ABOVE the 1/4 row that carries "7, 8, 9"
    // (form furniture such as "=  x  =" sits between them). The old rule took one token
    // before 1/4 and a row that did not fit simply started no block — six blocks across
    // Kingsley, Walker and Hartway were dropped without a word, 175.4 appraised acres.
    // Kevin approved the fix: read every field number, and a field the reader still cannot
    // take is named in `refused`, never dropped.
    const wrap = ID_WRAP.exec(l);
    if (wrap && !/1\/4/.test(l)) {
      idWrap = idWrap ? `${idWrap} ${clean(wrap[1])}` : clean(wrap[1]);
      continue;
    }
    // the field id leads the 1/4 row
    const fid = FID_ROW.exec(l);
    if (fid && !/^Field\b/i.test(fid[1])) {
      closeOpen();
      headerOpen = false;
      const id = clean(idWrap ? `${idWrap} ${fid[1]}` : fid[1]);
      idWrap = null;
      pending = { ...ctx, fieldId: id, acres: null, totalWeight: null, repSamples: null, acreAppraisal: null };
      continue;
    }
    // A 1/4 row whose lead is not a field id the reader can take (a name it cannot parse,
    // a scrawl): the block's numbers are still read so the refusal can carry them.
    const bare = /^\s*(.*?)\s*\b1\/4\s+\d/.exec(l);
    if (bare && bare[1] && !/^Field\b/i.test(bare[1])) {
      closeOpen();
      headerOpen = false;
      idWrap = null;
      orphan = { ...ctx, fieldId: null, idText: clean(bare[1]), acres: null, totalWeight: null, repSamples: null, acreAppraisal: null };
      continue;
    }

    // the acreage leads the Doughy row
    const ac = /^\s*([\d.]+)\s+Doughy\b/.exec(l);
    if (ac) {
      const open = pending || orphan;
      if (open && open.acres == null) { open.acres = num(ac[1]); continue; }
      // An acreage with no field block open: a block whose 1/4 row the reader did not
      // see at all. Named, with its acres, and the division that follows closes it.
      closeOpen();
      idWrap = null;
      orphan = { ...ctx, fieldId: null, idText: null, acres: num(ac[1]), totalWeight: null, repSamples: null, acreAppraisal: null };
      continue;
    }

    const end = BLOCK_END.exec(l);
    // …and the same row with its operators on a line of their own, which is what the
    // browser's extraction produces. Only accepted where the division actually divides.
    const split = !end && (pending || orphan) ? divisionRow(l) : null;
    const div = end ? { total: num(end[1]), samples: num(end[2]), appraisal: num(end[3]) } : split;
    if (div && pending) {
      pending.totalWeight = div.total;
      pending.repSamples = div.samples;
      pending.acreAppraisal = div.appraisal;
      out.push(pending);
      pending = null;
      idWrap = null;
      continue;
    }
    if (div && orphan) {
      orphan.totalWeight = div.total;
      orphan.repSamples = div.samples;
      orphan.acreAppraisal = div.appraisal;
      refused.push({ ...orphan, reason: orphan.idText
        ? `the field ID "${orphan.idText}" could not be read`
        : "a field block with no field ID the reader could find" });
      orphan = null;
      idWrap = null;
      continue;
    }
  }
  closeOpen();
  return { appraisals: out, refused };

  function closeOpen() {
    if (pending && pending.acreAppraisal == null) refused.push({ ...pending, reason: "no acre appraisal on the block" });
    if (orphan) refused.push({ ...orphan, reason: "a field block that never closed with an acre appraisal" });
    pending = null;
    orphan = null;
  }
}

// ── THE DOCUMENT ─────────────────────────────────────────────────────────────
export function parseAppraisalDocument(text) {
  const header = parseHeader(text);
  const { appraisals, refused } = parseAppraisals(text);

  // ⚠ AN APPRAISAL WITH NO ACREAGE CONTRIBUTES NO BUSHELS AND IS NAMED. Multiplying a
  // per-acre figure by an acreage nobody read is exactly the invention this repo forbids;
  // the row is kept so the reader can see it, and excluded from the total.
  const priced = appraisals.filter((a) => a.acres != null && a.acreAppraisal != null);
  const unpriced = appraisals.filter((a) => !(a.acres != null && a.acreAppraisal != null));
  const rows = priced.map((a) => ({ ...a, bushels: +(a.acres * a.acreAppraisal).toFixed(1) }));

  return {
    ...header,
    // Bushels of GRAIN. The form is a grain appraisal on corn being chopped — see header.
    unit: "bu",
    appraisals: rows.concat(unpriced.map((a) => ({ ...a, bushels: null }))),
    totals: {
      blocks: appraisals.length,
      acres: +priced.reduce((s, a) => s + a.acres, 0).toFixed(1),
      bushels: +rows.reduce((s, a) => s + a.bushels, 0).toFixed(1),
      fsaFarms: [...new Set(appraisals.map((a) => a.fsaFarmNumber).filter(Boolean))].length,
      withoutAcreage: unpriced.length,
      refused: refused.length,
    },
    refused,
    // ⚠ NEVER INFERRED. An appraisal covers the fields the adjuster walked; whether that is
    // the whole unit is not something the document can say, so it is not claimed here.
    coversWholeUnit: null,
  };
}

// ── STORAGE ROWS ─────────────────────────────────────────────────────────────
//
// `silage_appraisals` already exists and already has the right shape — `fsn`, `field_no`,
// `appraised_yield_bu_ac`, `appraised_acres`, `appraised_bu`, `yield_unit`. Nothing about
// the schema changes; this only maps the document onto it.
//
// ⚠ THE FARM IS RESOLVED BY POLICY NUMBER, NOT BY NAME. The document prints the insured's
// name and this book has three copies of a name-matching rule that have to be kept in step
// by hand — while the policy number on the form is the AIP's own key and lands on exactly
// one row. A document whose policy number resolves to nothing is REFUSED rather than
// attached to the closest name: an appraisal filed against the wrong grower is production
// counted against a crop they did not grow.
export function toStorageRows(doc, { farmerId, sourceFile = null } = {}) {
  if (!doc || !farmerId) return { rows: [], refused: ["no farmer — the policy number resolved to nobody"] };
  const d = doc;
  const iso = (s) => {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(s || ""));
    return m ? `${m[3]}-${m[1]}-${m[2]}` : null;
  };
  const rows = [], refused = [];
  d.appraisals.forEach((a, i) => {
    if (a.bushels == null) { refused.push(`field ${a.fieldId} on FSA farm ${a.fsaFarmNumber}: no acreage was read, so no bushels are claimed`); return; }
    rows.push({
      farmer_id: farmerId,
      crop_year: d.cropYear,
      crop: a.crop || "CORN",
      // The county of THIS block's own coverage header; the document's first header is
      // only the fallback. A worksheet spanning two counties prints each unit's county.
      county: a.county || d.county,
      fsn: a.fsaFarmNumber || null,
      field_no: a.fieldId || null,
      field_name: a.fieldId || null,
      unit_number: a.unitNumber || null,
      // The coverage TYPE this block's unit header prints ("016" Grain, "026" Silage):
      // how these acres are INSURED, which is what decides whether their bushels are grain
      // production-to-count (src/appraisalSheet.js appraisalInsuredType). Null when the
      // header did not state one, never a guess.
      type_code: a.typeCode || null,
      appraised_yield_bu_ac: a.acreAppraisal,
      appraised_acres: a.acres,
      appraised_bu: a.bushels,
      appraised_qty: a.bushels,
      // ⚠ BUSHELS OF GRAIN, AND THE COLUMN SAYS SO. The form is a grain appraisal on corn
      // being chopped (Type 016-Grain); calling it tons would be a different quantity.
      yield_unit: "bu",
      appraisal_date: iso(d.createdDate),
      claim_number: d.claimNumber || null,
      policy_number: d.policyNumber || null,
      source_file: sourceFile,
      source_part: "Part II - Mature Line Weight Method",
      line_no: i + 1,
      // ⚠ NOT A CLAIM. Kevin: "This may not be all of their acres so dont flag as claim
      // yet." The appraisal covers the fields the adjuster walked; the document cannot say
      // whether that is the unit, so the row says what it is and claims nothing more.
      check_status: "appraisal_only",
      check_detail: `${d.totals.blocks} field appraisals over ${d.totals.acres} acres on `
        + `${d.totals.fsaFarms} FSA farms; not reconciled to the unit's insured acres`,
      remarks: `total weight ${a.totalWeight} over ${a.repSamples} representative samples`,
    });
  });
  return { rows, refused };
}
